import EllipsisSrc from "@/assets/svg/animation/ellipsis.svg"
import EmailBackgroundSrc from "@/assets/svg/animation/email-background.svg"
import ReturnSrc from "@/assets/svg/animation/return.svg"
import anime, { AnimeTimelineInstance } from "animejs"
import parse from "html-react-parser"
import React, { FC, useEffect, useRef } from "react"
import styled from "styled-components"

import { ISequenceTiming, Omnichannel } from "./AnimationPlayer"
import { handleAvatarImage } from "./animationHelpers"

export interface IEmailScene {
  email?: {
    avatar: string
    from: string
    message: string
    title: string
    to: string
  }
  omnichannel: Omnichannel
  sequenceTiming: ISequenceTiming
}

// Audio base components
const BackgroundElements = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  z-index: -1;
  display: block;
  width: 100%;
  height: 100%;
  opacity: 0;

  /* Floating windows */
  &::before {
    position: absolute;
    top: 20%;
    left: 6%;
    display: block;
    width: 100%;
    max-width: 492px;
    height: 100%;
    max-height: 442px;
    content: "";
    background: url("${EmailBackgroundSrc}");
  }

  /* Gradient circle falloff effect */
  &::after {
    position: absolute;
    top: calc(50% - 320px);
    left: calc(50% - 280px);
    display: block;
    width: 100%;
    max-width: 700px;
    height: 100%;
    max-height: 700px;
    content: "";
    background: radial-gradient(
      rgba(173, 159, 255, 0.22) 0%,
      rgba(173, 159, 255, 0) 60%,
      rgba(173, 159, 255, 0) 100%
    );
  }
`
const EmailMessage = styled.div`
  display: flex;
  flex-flow: column;
  width: 100%;
  max-width: 300px;
  margin: 0 auto;
  color: white;
  background: #1f212d;
  border-radius: 8px;
  box-shadow: inset 0 0 0 1px rgba(236, 244, 255, 0.8);
`
const EmailHeader = styled.span`
  display: flex;
  align-items: center;
  height: 50px;
  padding: 0 16px;
  font-size: 14px;
  font-weight: bold;
  line-height: 18px;
  border-bottom: 1px solid rgba(236, 244, 255, 0.8);
`
const EmailInfoBar = styled.div`
  display: flex;
  gap: 0 12px;
  align-items: center;
  justify-content: flex-start;
  padding: 16px;
  font-size: 14px;
  line-height: 20px;

  &::before {
    display: block;
    order: 2;
    width: 20px;
    height: 20px;
    margin-left: auto;
    content: "";
    background: url("${ReturnSrc}") no-repeat center center;
  }

  &::after {
    display: block;
    order: 3;
    width: 20px;
    height: 20px;
    content: "";
    background: url("${EllipsisSrc}") no-repeat center center;
  }
`
const EmailSenderAvatar = styled.img`
  width: 40px;
  height: 40px;
  border-radius: 40px;
`
const EmailSenderInfo = styled.div`
  display: flex;
  flex-direction: column;
  order: 1;
`
const EmailSenderName = styled.span`
  font-weight: bold;
`
const EmailReceiverName = styled.span``
const EmailBody = styled.div`
  padding: 0 16px 16px;
  font-size: 14px;
  line-height: 22px;

  > p {
    margin: 0;

    em {
      font-style: normal;
      color: #ad9fff;
    }
  }
`

export const Email: FC<IEmailScene> = ({ email, sequenceTiming }) => {
  if (!email) return null

  // Refs
  const timelineRef = useRef<AnimeTimelineInstance>()
  const BackgroundElementsRef = useRef<HTMLDivElement>(null)
  const EmailMessageRef = useRef<HTMLDivElement>(null)

  // Effect handling
  useEffect(() => {
    // Handle Anime.js timeline
    timelineRef.current = anime.timeline({
      autoplay: true,

      easing: "easeOutCubic",
      loop: false,
    })

    // Animate items via Timeline
    if (timelineRef.current && email) {
      timelineRef.current
        // Background elements
        .add({
          duration: 1000,
          opacity: [0, 1],
          scale: [0.75, 1],
          targets: BackgroundElementsRef.current,
        })
        // Email message
        .add(
          {
            duration: 900,
            opacity: [0, 1],
            scale: [0.66, 1],
            targets: EmailMessageRef.current,
            translateY: [35, 0],
          },
          "-=650",
        )
        .add({
          delay: sequenceTiming.timeBetweenScenes,
          duration: 1600,
          opacity: [1, 0],
          scale: [1, 0.33],
          targets: BackgroundElementsRef.current,
          translateY: [0, 35],
        })
        .add(
          {
            begin: () => {
              // Dispatch event to parent component
              window.dispatchEvent(
                new CustomEvent("pxmPreTransitionSceneComplete"),
              )
            },
            duration: 1300,
            opacity: [1, 0],
            scale: [1, 0.6],
            targets: EmailMessageRef.current,
            translateY: [0, -25],
          },
          "-=1300",
        )
    }

    // Cleanup
    return () => {
      timelineRef.current?.pause()
      timelineRef.current = undefined
    }
  }, [])

  return (
    <>
      <BackgroundElements ref={BackgroundElementsRef} />
      <EmailMessage ref={EmailMessageRef}>
        <EmailHeader>{email.title}</EmailHeader>
        <EmailInfoBar>
          <EmailSenderAvatar src={handleAvatarImage(email.avatar)} />
          <EmailSenderInfo>
            <EmailSenderName>{email.from}</EmailSenderName>
            <EmailReceiverName>{email.to}</EmailReceiverName>
          </EmailSenderInfo>
        </EmailInfoBar>
        <EmailBody>{parse(email.message)}</EmailBody>
      </EmailMessage>
    </>
  )
}
